import { LIBRARY_BASE_URL } from '@/consts/urls';
import getPublicHeaders from '@/requests/common/getPublicHeaders';
import { isGenaiWebHost, isPortfolioApp } from '@/utils/environment/surface';

/**
 * Absolute URL for a CMS path.
 *
 * The portfolio app reads the `/portfolio` sibling of every CMS route
 * (`{LIBRARY_URL}/portfolio/…`), which is curated for the standalone GenAI
 * app — the default routes are filtered down to what the Picsart surfaces may
 * show, and the two catalogues are now maintained separately in the CMS.
 * gen.ai web is a portfolio surface too, but reads the `/full` sibling instead.
 * It is checked first for that reason. Every other surface reads the default
 * routes.
 * Resolved per request rather than baked into a const: the surface is only
 * known once the host's bootstrap payload has landed.
 *
 * Callers must therefore fetch no earlier than the host's `onOpen` — the
 * touchpoint header `isPortfolioApp()` reads arrives in that payload, and
 * before it lands `getHeaders()` is `{}` and every surface reads as "not
 * portfolio". Nothing enforces this at runtime: an earlier caller silently
 * gets the filtered catalogue, cached under a surface-agnostic query key for
 * the whole `staleTime`. The earliest caller today is `prefetchPresetFeedData`
 * from `App`'s `useOpen` callback, which is exactly that boundary.
 */
const getLibrarySurfacePrefix = (): string => {
  if (isGenaiWebHost()) return '/full';
  if (isPortfolioApp()) return '/portfolio';
  return '';
};

const buildLibraryUrl = (path: string): string =>
  `${LIBRARY_BASE_URL}${getLibrarySurfacePrefix()}${path}`;

interface FetchLibraryDataParams<T> {
  /** CMS path, base excluded — see `buildLibraryUrl`. */
  path: string;
  signal?: AbortSignal;
  transform: (data: unknown) => T | null;
}

export const fetchLibraryData = async <T>({
  path,
  signal,
  transform,
}: FetchLibraryDataParams<T>): Promise<T | null> => {
  try {
    const response = await fetch(buildLibraryUrl(path), {
      headers: getPublicHeaders(),
      signal,
    });
    if (!response.ok) return null;
    const data: unknown = await response.json();
    return transform(data);
  } catch {
    return null;
  }
};
